// Custom React hook for section data fetching with React Query
import { useQuery, UseQueryOptions, UseQueryResult, QueryClient } from '@tanstack/react-query';
import { sectionService } from '@/services/sectionService';
import type { SectionWithGames, ApiError } from '@/services/api-client';

// Query key factory for consistent cache keys
export const sectionKeys = {
  all: ['sections'] as const,
  lists: () => [...sectionKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...sectionKeys.lists(), { filters }] as const,
  details: () => [...sectionKeys.all, 'detail'] as const,
  detail: (id: number) => [...sectionKeys.details(), id] as const,
};

// Hook options interface
interface UseSectionsOptions {
  /**
   * Only return active sections (default: true)
   */
  activeOnly?: boolean;
  
  /**
   * Enable/disable the query (default: true)
   */
  enabled?: boolean;
  
  /**
   * Additional React Query options
   */
  queryOptions?: Partial<UseQueryOptions<SectionWithGames[], ApiError>>;
}

// Hook result interface
export interface UseSectionsResult extends UseQueryResult<SectionWithGames[], ApiError> {
  /**
   * Filtered sections (active only if activeOnly = true)
   */
  sections: SectionWithGames[] | undefined;
  
  /**
   * Manual refetch function
   */
  refetch: () => void;
  
  /**
   * Check if currently fetching
   */
  isLoading: boolean;
  
  /**
   * Check if there's an error
   */
  error: ApiError | null;
}

/**
 * Custom hook for fetching all sections with React Query caching
 */
export function useSections(options: UseSectionsOptions = {}): UseSectionsResult {
  const {
    activeOnly = true,
    enabled = true,
    queryOptions = {}
  } = options;

  const query = useQuery({
    queryKey: sectionKeys.list({ activeOnly }),
    queryFn: async (): Promise<SectionWithGames[]> => {
      const sections = await sectionService.getSections();
      
      if (activeOnly) {
        return sectionService.filterActiveSections(sections);
      }
      
      return sections;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes (match backend cache TTL)
    gcTime: 30 * 60 * 1000,   // 30 minutes (garbage collection time)
    retry: (failureCount, error) => {
      // Don't retry client errors (4xx), only server errors (5xx) and network errors
      const apiError = error as ApiError;
      if (apiError.status && apiError.status >= 400 && apiError.status < 500) {
        return false;
      }
      return failureCount < 2; // Retry up to 2 times for retryable errors
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff, max 10s
    ...queryOptions
  });

  return {
    ...query,
    sections: query.data,
    refetch: () => {
      query.refetch();
    },
    isLoading: query.isLoading,
    error: query.error
  };
}

// Hook options interface for single section
interface UseSectionOptions {
  /**
   * Enable/disable the query (default: true)
   */
  enabled?: boolean;
  
  /**
   * Additional React Query options
   */
  queryOptions?: Partial<UseQueryOptions<SectionWithGames, ApiError>>;
}

// Hook result interface for single section
export interface UseSectionResult extends UseQueryResult<SectionWithGames, ApiError> {
  /**
   * The section data
   */
  section: SectionWithGames | undefined;
  
  /**
   * Manual refetch function
   */
  refetch: () => void;
  
  /**
   * Check if currently fetching
   */
  isLoading: boolean;
  
  /**
   * Check if there's an error
   */
  error: ApiError | null;
}

/**
 * Custom hook for fetching a single section by ID with React Query caching
 */
export function useSection(sectionId: number, options: UseSectionOptions = {}): UseSectionResult {
  const {
    enabled = true,
    queryOptions = {}
  } = options;

  const query = useQuery({
    queryKey: sectionKeys.detail(sectionId),
    queryFn: (): Promise<SectionWithGames> => sectionService.getSectionById(sectionId),
    enabled: enabled && sectionId > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes (match backend cache TTL)
    gcTime: 30 * 60 * 1000,   // 30 minutes (garbage collection time)
    retry: (failureCount, error) => {
      // Don't retry client errors (4xx), only server errors (5xx) and network errors
      const apiError = error as ApiError;
      if (apiError.status && apiError.status >= 400 && apiError.status < 500) {
        return false;
      }
      return failureCount < 2; // Retry up to 2 times for retryable errors
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff, max 10s
    ...queryOptions
  });

  return {
    ...query,
    section: query.data,
    refetch: () => {
      query.refetch();
    },
    isLoading: query.isLoading,
    error: query.error
  };
}

/**
 * Utility function to invalidate section cache
 */
export function invalidateSectionCache(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: sectionKeys.all
  });
}

/**
 * Utility function to prefetch sections
 */
export function prefetchSections(queryClient: QueryClient, activeOnly = true): Promise<void> {
  return queryClient.prefetchQuery({
    queryKey: sectionKeys.list({ activeOnly }),
    queryFn: async (): Promise<SectionWithGames[]> => {
      const sections = await sectionService.getSections();
      
      if (activeOnly) {
        return sectionService.filterActiveSections(sections);
      }
      
      return sections;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}